import {useMemo} from 'react';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  AnalyticsGraphMetric,
  AnalyticsMetricError,
  AnalyticsMultiGraphMetric,
  AnalyticsTableMetric,
} from '../../../../models/api/analyticsMetricsModel';
import {makeGetAnalyticsMetric} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {isComputedGraphMetric} from '../../../helpers/reportHelpers/analyticsReportHelpers';
import {getComputedMetricTransformer} from '../../../transformers/analyticsMetricsTransformers';
import {AnyGraphMetric, AnyGraphMetricsEnum} from '../helperTypes';

// TODO get rid of this v3 is fully rolled-out.
export interface MockedGraphMetricData<T> {
  values: ReadonlyArray<T>;
  isMocked: boolean;
  isEmpty?: boolean;
}

export function useGraphMetricData<T, U extends AnyGraphMetric>(
  selectedMetric: AnyGraphMetricsEnum | undefined,
  loadingStatePredicate: (metricData?: U) => boolean,
  mockData: ReadonlyArray<T>,
): [U | MockedGraphMetricData<T> | undefined, AnalyticsMetricError | undefined] {
  const localize = useLocalize();

  const [storeMetricData, metricError] = useAppSelector(
    makeGetAnalyticsMetric(
      isComputedGraphMetric(selectedMetric)
        ? getComputedMetricTransformer(selectedMetric).from
        : selectedMetric,
    ),
  );

  const graphMetricData = useMemo(
    () =>
      isComputedGraphMetric(selectedMetric)
        ? getComputedMetricTransformer(selectedMetric).transformer(
            storeMetricData as AnalyticsTableMetric | undefined,
            localize,
          )
        : (storeMetricData as AnalyticsGraphMetric | AnalyticsMultiGraphMetric | undefined),
    [localize, selectedMetric, storeMetricData],
  ) as U | undefined;

  if (!metricError && loadingStatePredicate(graphMetricData)) {
    return [
      {
        values: mockData,
        isMocked: true,
      },
      undefined,
    ];
  }

  return [graphMetricData, metricError];
}
